<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useDataSourcesStore } from '@product/stores/dataSources'
import { getLogTypeColor } from '@/constants/logTypes'

const props = defineProps({
  mode: {
    type: String,
    default: 'store',
    validator: v => ['store', 'local'].includes(v)
  },
  modelValue: {
    type: Object,
    default: null
  },
  showLogTypes: {
    type: Boolean,
    default: true
  },
  disabled: {
    type: Boolean,
    default: false
  },
  size: {
    type: String,
    default: 'default',
    validator: v => ['default', 'small'].includes(v)
  },
  // Keep the trigger in its filled state even with nothing filtered. For
  // toolbars where it's a primary control and should read as a button rather
  // than as a filter indicator.
  alwaysEnabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const store = useDataSourcesStore()

// --- Local selection state (used when mode='local') ---
const localSelectedSources = ref([])
const localSelectedLogTypes = ref([])
// Sync modelValue → local state
watch(() => props.modelValue, (val) => {
  if (props.mode !== 'local') return
  localSelectedSources.value = val?.sources ? [...val.sources] : []
  localSelectedLogTypes.value = val?.logTypes ? [...val.logTypes] : []
}, { immediate: true })

function emitLocal() {
  emit('update:modelValue', {
    sources: [...localSelectedSources.value],
    logTypes: [...localSelectedLogTypes.value],
  })
}

// --- UI state ---
const isOpen = ref(false)
const triggerRef = ref(null)
const menuRef = ref(null)
const menuStyle = ref({})
const expandedSources = ref({})
// Member-tier expansion for owned groups (separate from log-type expansion above)
const expandedGroups = ref({})

// --- Owned-group drill-in helpers (work across both modes) ---
function isExpandableGroup(name) {
  return !!store.expandableGroups[name]
}
function getGroupMembers(name) {
  return store.expandableGroups[name] || []
}
function getGroupMemberNames(name) {
  return store.groupMemberNames[name] || []
}

// --- Source list (always the same shape) ---
const activeSources = computed(() => {
  const items = []
  for (const g of store.activeSourceGroups) {
    items.push({ ...g, isGroup: true })
  }
  for (const s of store.activeStandaloneSources) {
    items.push({ name: s.name, display_name: s.display_name, isGroup: false })
  }
  return items
})

// --- Accessors that work across modes ---
const selectedSources = computed(() =>
  props.mode === 'store' ? store.selectedSources : localSelectedSources.value
)
const selectedLogTypes = computed(() =>
  props.mode === 'store' ? store.selectedLogTypes : localSelectedLogTypes.value
)
const isAllSelected = computed(() => {
  if (props.mode === 'store') return store.isAllSelected
  return selectedSources.value.length === 0 && selectedLogTypes.value.length === 0
})
const isFiltered = computed(() => !isAllSelected.value)

// --- Label (unified across modes) ---
const selectorLabel = computed(() => {
  if (props.mode === 'store') return store.selectorLabel
  if (!store.hasSources) return 'No Sources'
  if (isAllSelected.value) return 'All Sources'

  const parts = []
  if (selectedSources.value.length > 0) {
    if (selectedSources.value.length === 1) {
      const name = selectedSources.value[0]
      const group = store.sourceGroups.find(g => g.name === name)
      const standalone = store.standaloneSources.find(s => s.name === name)
      parts.push(group ? group.display_name : standalone ? standalone.display_name : name)
    } else {
      parts.push(`${selectedSources.value.length} Sources`)
    }
  }
  if (props.showLogTypes && selectedLogTypes.value.length > 0) {
    if (selectedLogTypes.value.length === 1) parts.push(selectedLogTypes.value[0])
    else parts.push(`${selectedLogTypes.value.length} logs`)
  }
  return parts.length > 0 ? parts.join(' \u00B7 ') : 'All Sources'
})

// --- Selection logic ---
// Per-source log types (members, standalone sources).
function getSourceLogTypes(sourceName) {
  return store.sourceLogTypeMap[sourceName] || []
}

// Group-level log types merged across all member sources. Falls back to the
// per-source map for standalone sources (which are never in groupLogTypeMap).
function getGroupLogTypes(groupName) {
  return store.groupLogTypeMap[groupName] || store.sourceLogTypeMap[groupName] || []
}

function isSourceFullySelected(sourceName) {
  if (props.mode === 'store') return store.isSourceFullySelected(sourceName)
  if (isAllSelected.value) return true
  if (selectedSources.value.includes(sourceName)) return true
  const members = getGroupMemberNames(sourceName)
  if (members.length > 0 && members.every(m => selectedSources.value.includes(m))) return true
  if (!props.showLogTypes) return false
  const logTypes = getGroupLogTypes(sourceName)
  if (logTypes.length === 0) return false
  return logTypes.every(lt => selectedLogTypes.value.includes(lt))
}

function isSourcePartiallySelected(sourceName) {
  if (props.mode === 'store') return store.isSourcePartiallySelected(sourceName)
  if (selectedSources.value.includes(sourceName)) return false
  const members = getGroupMemberNames(sourceName)
  if (members.length > 0) {
    const count = members.filter(m => selectedSources.value.includes(m)).length
    if (count === members.length) return false
    if (count > 0) return true
  }
  if (!props.showLogTypes) return false
  const logTypes = getGroupLogTypes(sourceName)
  if (logTypes.length === 0) return false
  const count = logTypes.filter(lt => selectedLogTypes.value.includes(lt)).length
  return count > 0 && count < logTypes.length
}

function isMemberSelected(groupName, memberName) {
  if (props.mode === 'store') return store.isMemberSelected(groupName, memberName)
  if (isAllSelected.value) return true
  if (selectedSources.value.includes(memberName)) return true
  if (selectedSources.value.includes(groupName)) return true
  return false
}

function getSourceCheckState(sourceName) {
  if (isSourceFullySelected(sourceName)) return 'full'
  if (isSourcePartiallySelected(sourceName)) return 'partial'
  return 'none'
}

function isLogTypeSelected(logType) {
  if (props.mode === 'store') {
    return selectedLogTypes.value.length === 0 || selectedLogTypes.value.includes(logType)
  }
  if (isAllSelected.value) return true
  if (selectedLogTypes.value.includes(logType)) return true
  for (const source of activeSources.value) {
    if (selectedSources.value.includes(source.name) && getSourceLogTypes(source.name).includes(logType)) {
      return true
    }
  }
  return false
}

// --- Toggle handlers (unified for local mode) ---
function handleSourceToggle(sourceName) {
  // Owned, expandable groups have member-aware toggle semantics.
  if (isExpandableGroup(sourceName)) {
    handleGroupToggle(sourceName)
    return
  }
  if (props.mode === 'store') {
    store.toggleSource(sourceName)
    return
  }

  if (isAllSelected.value) {
    // Transition from "all" to "all except this one"
    const remaining = activeSources.value
      .map(s => s.name)
      .filter(n => n !== sourceName)
    if (remaining.length === 0) return // Can't deselect the only source
    localSelectedSources.value = remaining
    localSelectedLogTypes.value = []
  } else if (isSourceFullySelected(sourceName)) {
    // Deselect source and its log types
    const nextSources = localSelectedSources.value.filter(s => s !== sourceName)
    const logTypes = getSourceLogTypes(sourceName)
    const nextLogTypes = localSelectedLogTypes.value.filter(lt => !logTypes.includes(lt))
    // Don't allow deselecting the last source
    if (nextSources.length === 0 && nextLogTypes.length === 0) return
    localSelectedSources.value = nextSources
    localSelectedLogTypes.value = nextLogTypes
  } else {
    // Select source
    if (!localSelectedSources.value.includes(sourceName)) {
      localSelectedSources.value.push(sourceName)
    }
    // Remove individual log types for this source (source-level selection covers them)
    const logTypes = getSourceLogTypes(sourceName)
    localSelectedLogTypes.value = localSelectedLogTypes.value.filter(lt => !logTypes.includes(lt))
    // If all sources now selected, reset to empty (= all)
    if (localSelectedSources.value.length >= activeSources.value.length && localSelectedLogTypes.value.length === 0) {
      localSelectedSources.value = []
    }
  }
  emitLocal()
}

// Toggle an owned group as a whole (clears member/log-type sub-selections).
function handleGroupToggle(groupName) {
  if (props.mode === 'store') {
    store.toggleGroup(groupName)
    return
  }
  localToggleGroup(groupName)
}

function localToggleGroup(groupName) {
  const members = getGroupMemberNames(groupName)
  const groupLogTypes = getGroupLogTypes(groupName)

  if (isSourceFullySelected(groupName) && !isAllSelected.value) {
    const remove = new Set([groupName, ...members])
    const nextSources = localSelectedSources.value.filter(s => !remove.has(s))
    const nextLogTypes = localSelectedLogTypes.value.filter(lt => !groupLogTypes.includes(lt))
    if (nextSources.length === 0 && nextLogTypes.length === 0) return
    localSelectedSources.value = nextSources
    localSelectedLogTypes.value = nextLogTypes
  } else {
    const memberSet = new Set(members)
    const nextSources = localSelectedSources.value.filter(s => !memberSet.has(s))
    if (!nextSources.includes(groupName)) nextSources.push(groupName)
    localSelectedSources.value = nextSources
    localSelectedLogTypes.value = localSelectedLogTypes.value.filter(lt => !groupLogTypes.includes(lt))
  }
  emitLocal()
}

// Toggle a single member source of an owned group.
function handleMemberToggle(groupName, memberName) {
  if (props.mode === 'store') {
    store.toggleMember(groupName, memberName)
    return
  }
  localToggleMember(groupName, memberName)
}

function localToggleMember(groupName, memberName) {
  const members = getGroupMemberNames(groupName)

  if (isAllSelected.value) {
    localSelectedSources.value = [memberName]
    localSelectedLogTypes.value = []
    emitLocal()
    return
  }

  let working = [...localSelectedSources.value]
  const groupIdx = working.indexOf(groupName)
  if (groupIdx !== -1) {
    working.splice(groupIdx, 1)
    for (const m of members) if (!working.includes(m)) working.push(m)
  }

  const memberIdx = working.indexOf(memberName)
  if (memberIdx !== -1) working.splice(memberIdx, 1)
  else working.push(memberName)

  if (working.length === 0 && localSelectedLogTypes.value.length === 0) return

  if (members.length > 0 && members.every(m => working.includes(m))) {
    working = working.filter(m => !members.includes(m))
    working.push(groupName)
  }

  localSelectedSources.value = working
  emitLocal()
}

function handleLogTypeToggle(logType) {
  if (props.mode === 'store') {
    store.toggleLogType(logType)
    return
  }

  if (isAllSelected.value) {
    localSelectedSources.value = []
    const allLogTypes = new Set()
    for (const source of activeSources.value) {
      for (const lt of getSourceLogTypes(source.name)) {
        allLogTypes.add(lt)
      }
    }
    allLogTypes.delete(logType)
    localSelectedLogTypes.value = [...allLogTypes]
    for (const source of activeSources.value) {
      if (getSourceLogTypes(source.name).length === 0) {
        localSelectedSources.value.push(source.name)
      }
    }
  } else {
    const idx = localSelectedLogTypes.value.indexOf(logType)
    if (idx >= 0) {
      localSelectedLogTypes.value.splice(idx, 1)
    } else {
      localSelectedLogTypes.value.push(logType)
    }
    for (const source of activeSources.value) {
      const sourceLts = getSourceLogTypes(source.name)
      if (localSelectedSources.value.includes(source.name) && sourceLts.includes(logType)) {
        localSelectedSources.value = localSelectedSources.value.filter(s => s !== source.name)
        for (const lt of sourceLts) {
          if (lt !== logType && !localSelectedLogTypes.value.includes(lt)) {
            localSelectedLogTypes.value.push(lt)
          }
        }
      }
    }
  }
  emitLocal()
}

function handleReset() {
  if (props.mode === 'store') {
    store.resetSelection()
  } else {
    localSelectedSources.value = []
    localSelectedLogTypes.value = []
    emitLocal()
  }
  close()
}

// --- Dropdown UI ---
function toggleSourceExpand(sourceId, event) {
  event.stopPropagation()
  expandedSources.value = {
    ...expandedSources.value,
    [sourceId]: !expandedSources.value[sourceId]
  }
  nextTick(updateMenuPosition)
}

function toggleGroupExpand(groupName, event) {
  event.stopPropagation()
  expandedGroups.value = {
    ...expandedGroups.value,
    [groupName]: !expandedGroups.value[groupName]
  }
  nextTick(updateMenuPosition)
}

// Per-group display mode for owned groups: 'sources' (drill into member sources,
// each with its own log types) or 'logtypes' (the group's log types merged across
// all member sources). Defaults to 'sources'.
const groupViewMode = ref({})

function getGroupViewMode(groupName) {
  return groupViewMode.value[groupName] || 'sources'
}

function toggleGroupViewMode(groupName, event) {
  event.stopPropagation()
  const next = getGroupViewMode(groupName) === 'sources' ? 'logtypes' : 'sources'
  groupViewMode.value = { ...groupViewMode.value, [groupName]: next }
  // Expand the group so the chosen view is visible immediately.
  expandedGroups.value = { ...expandedGroups.value, [groupName]: true }
  nextTick(updateMenuPosition)
}

function updateMenuPosition() {
  if (!triggerRef.value || !isOpen.value) return
  const rect = triggerRef.value.getBoundingClientRect()
  const menuWidth = props.size === 'small' ? 240 : 260
  const menuHeight = menuRef.value?.offsetHeight || 300

  let left = rect.left
  let top = rect.bottom + 6

  if (top + menuHeight > window.innerHeight - 10) {
    top = rect.top - menuHeight - 6
  }
  if (left + menuWidth > window.innerWidth - 10) {
    left = window.innerWidth - menuWidth - 10
  }
  if (left < 10) left = 10

  menuStyle.value = {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
    width: `${menuWidth}px`
  }
}

function toggle(event) {
  event.stopPropagation()
  isOpen.value = !isOpen.value
}

function close() {
  isOpen.value = false
}

watch(isOpen, async (val) => {
  if (val) {
    if (selectedLogTypes.value.length > 0 || selectedSources.value.length > 0) {
      for (const source of activeSources.value) {
        // Reveal owned groups whose members are individually selected.
        if (isExpandableGroup(source.name)) {
          if (isSourcePartiallySelected(source.name)) {
            expandedGroups.value[source.name] = true
          }
        } else if (props.showLogTypes && (isSourcePartiallySelected(source.name) || isSourceFullySelected(source.name))) {
          expandedSources.value[source.name] = true
        }
      }
    }
    await nextTick()
    updateMenuPosition()
  }
})

function handleClickOutside(event) {
  if (triggerRef.value && triggerRef.value.contains(event.target)) return
  if (menuRef.value && menuRef.value.contains(event.target)) return
  close()
}

function handleKeydown(event) {
  if (event.key === 'Escape') close()
}

onMounted(() => {
  if (!store.initialized) {
    store.init()
  }
  document.addEventListener('click', handleClickOutside, true)
  document.addEventListener('keydown', handleKeydown)
  window.addEventListener('resize', updateMenuPosition)
  window.addEventListener('scroll', updateMenuPosition, true)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside, true)
  document.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('resize', updateMenuPosition)
  window.removeEventListener('scroll', updateMenuPosition, true)
})
</script>

<template>
  <div class="source-selector">
    <button
      type="button"
      ref="triggerRef"
      class="dropdown-trigger"
      :class="{
        'is-open': isOpen,
        'is-enabled': isFiltered || alwaysEnabled,
        'size-small': size === 'small'
      }"
      :disabled="disabled"
      @click="toggle"
    >
      <font-awesome-icon :icon="['fas', 'database']" class="dropdown-trigger-icon" />
      <span class="dropdown-trigger-value">{{ selectorLabel }}</span>
      <button
        v-if="isFiltered"
        type="button"
        class="reset-x"
        @click.stop="handleReset"
        title="Reset to all sources"
      >
        <font-awesome-icon :icon="['fas', 'xmark']" />
      </button>
      <font-awesome-icon v-else :icon="['fas', 'chevron-down']" class="dropdown-trigger-chevron" />
    </button>

    <Teleport to="body">
      <Transition name="selector-dropdown">
        <div
          v-if="isOpen"
          ref="menuRef"
          class="selector-menu"
          :style="menuStyle"
          @click.stop
        >
          <!-- Empty state -->
          <div v-if="activeSources.length === 0" class="empty-sources">
            <font-awesome-icon :icon="['fas', 'database']" class="empty-icon" />
            <span class="empty-text">No sources configured</span>
            <span class="empty-hint">Ask your administrator to add data sources to your organization.</span>
          </div>

          <!-- Source rows -->
          <div
            v-for="source in activeSources"
            :key="source.name"
            class="source-group"
          >
            <div
              class="menu-option source-row"
              :class="{ 'is-expanded': isExpandableGroup(source.name) ? expandedGroups[source.name] : (showLogTypes && source.isGroup && expandedSources[source.name]) }"
              @click="handleSourceToggle(source.name)"
            >
              <span class="option-check-space">
                <span
                  class="checkbox"
                  :class="{
                    'is-checked': getSourceCheckState(source.name) === 'full',
                    'is-indeterminate': getSourceCheckState(source.name) === 'partial'
                  }"
                >
                  <font-awesome-icon
                    v-if="getSourceCheckState(source.name) === 'full'"
                    :icon="['fas', 'check']"
                    class="check-icon"
                  />
                  <font-awesome-icon
                    v-else-if="getSourceCheckState(source.name) === 'partial'"
                    :icon="['fas', 'minus']"
                    class="check-icon"
                  />
                </span>
              </span>
              <span class="option-label source-label">{{ source.display_name }}</span>
              <!-- Owned group: switch between member-source view and group log-type view -->
              <button
                v-if="isExpandableGroup(source.name) && showLogTypes"
                type="button"
                class="view-mode-toggle"
                :title="getGroupViewMode(source.name) === 'sources' ? 'Show log types across all sources' : 'Show individual sources'"
                @click="toggleGroupViewMode(source.name, $event)"
              >
                <font-awesome-icon
                  :icon="['fas', getGroupViewMode(source.name) === 'sources' ? 'layer-group' : 'server']"
                />
              </button>
              <!-- Owned group: expand to member sources -->
              <button
                v-if="isExpandableGroup(source.name)"
                type="button"
                class="expand-chevron"
                :title="expandedGroups[source.name] ? 'Collapse' : 'Expand'"
                @click="toggleGroupExpand(source.name, $event)"
              >
                <font-awesome-icon
                  :icon="['fas', expandedGroups[source.name] ? 'chevron-up' : 'chevron-down']"
                />
              </button>
              <!-- Other groups / standalone sources: expand to log types -->
              <button
                v-else-if="showLogTypes"
                type="button"
                class="expand-chevron"
                @click="toggleSourceExpand(source.name, $event)"
              >
                <font-awesome-icon
                  :icon="['fas', expandedSources[source.name] ? 'chevron-up' : 'chevron-down']"
                />
              </button>
            </div>

            <template v-if="isExpandableGroup(source.name)">
            <!-- Member sources (owned, expandable group — 'sources' view) -->
            <Transition name="logs-expand">
              <div v-if="expandedGroups[source.name] && getGroupViewMode(source.name) === 'sources'" class="members-list">
                <div
                  v-for="member in getGroupMembers(source.name)"
                  :key="member.token"
                  class="member-group"
                >
                  <div
                    class="menu-option member-row"
                    @click="handleMemberToggle(source.name, member.token)"
                  >
                    <span class="option-check-space">
                      <span
                        class="checkbox"
                        :class="{ 'is-checked': isMemberSelected(source.name, member.token) }"
                      >
                        <font-awesome-icon
                          v-if="isMemberSelected(source.name, member.token)"
                          :icon="['fas', 'check']"
                          class="check-icon"
                        />
                      </span>
                    </span>
                    <span class="option-label member-label">{{ member.display_name }}</span>
                    <button
                      v-if="showLogTypes && getSourceLogTypes(member.name).length > 0"
                      type="button"
                      class="expand-chevron"
                      @click="toggleSourceExpand(member.token, $event)"
                    >
                      <font-awesome-icon
                        :icon="['fas', expandedSources[member.token] ? 'chevron-up' : 'chevron-down']"
                      />
                    </button>
                  </div>

                  <!-- Member's log types -->
                  <Transition v-if="showLogTypes" name="logs-expand">
                    <div v-if="expandedSources[member.token]" class="log-types-list member-logs">
                      <div
                        v-for="logType in getSourceLogTypes(member.name)"
                        :key="logType"
                        class="menu-option log-type-row"
                        :class="{ 'is-selected': isLogTypeSelected(logType) }"
                        @click="handleLogTypeToggle(logType)"
                      >
                        <span class="option-check-space">
                          <span
                            class="checkbox small"
                            :class="{ 'is-checked': isLogTypeSelected(logType) }"
                          >
                            <font-awesome-icon
                              v-if="isLogTypeSelected(logType)"
                              :icon="['fas', 'check']"
                              class="check-icon"
                            />
                          </span>
                        </span>
                        <span
                          class="log-type-dot"
                          :style="{ background: getLogTypeColor(logType) }"
                        ></span>
                        <span class="option-label">{{ logType }}</span>
                      </div>
                    </div>
                  </Transition>
                </div>
              </div>
            </Transition>

            <!-- 'logtypes' view: the group's log types merged across all member sources -->
            <Transition name="logs-expand">
              <div v-if="expandedGroups[source.name] && getGroupViewMode(source.name) === 'logtypes'" class="log-types-list">
                <div
                  v-for="logType in getGroupLogTypes(source.name)"
                  :key="logType"
                  class="menu-option log-type-row"
                  :class="{ 'is-selected': isLogTypeSelected(logType) }"
                  @click="handleLogTypeToggle(logType)"
                >
                  <span class="option-check-space">
                    <span
                      class="checkbox small"
                      :class="{ 'is-checked': isLogTypeSelected(logType) }"
                    >
                      <font-awesome-icon
                        v-if="isLogTypeSelected(logType)"
                        :icon="['fas', 'check']"
                        class="check-icon"
                      />
                    </span>
                  </span>
                  <span
                    class="log-type-dot"
                    :style="{ background: getLogTypeColor(logType) }"
                  ></span>
                  <span class="option-label">{{ logType }}</span>
                </div>
              </div>
            </Transition>
            </template>

            <!-- Log types (non-expandable group / standalone source) -->
            <Transition v-else-if="showLogTypes" name="logs-expand">
              <div v-if="expandedSources[source.name]" class="log-types-list">
                <div
                  v-for="logType in getGroupLogTypes(source.name)"
                  :key="logType"
                  class="menu-option log-type-row"
                  :class="{ 'is-selected': isLogTypeSelected(logType) }"
                  @click="handleLogTypeToggle(logType)"
                >
                  <span class="option-check-space">
                    <span
                      class="checkbox small"
                      :class="{ 'is-checked': isLogTypeSelected(logType) }"
                    >
                      <font-awesome-icon
                        v-if="isLogTypeSelected(logType)"
                        :icon="['fas', 'check']"
                        class="check-icon"
                      />
                    </span>
                  </span>
                  <span
                    class="log-type-dot"
                    :style="{ background: getLogTypeColor(logType) }"
                  ></span>
                  <span class="option-label">{{ logType }}</span>
                </div>
              </div>
            </Transition>
          </div>

          <!-- Reset to All -->
          <template v-if="isFiltered">
            <div class="menu-divider"></div>
            <div
              class="menu-option reset-option"
              @click="handleReset"
            >
              <span class="option-check-space">
                <font-awesome-icon :icon="['fas', 'rotate-left']" class="reset-icon" />
              </span>
              <span class="option-label">Reset to All</span>
            </div>
          </template>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.source-selector {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

/* Trigger base styles (.dropdown-trigger and children) come from
 * @/styles/dropdown-trigger.css. The size="small" prop applies a slightly
 * tighter density on top — useful in inline contexts like a SignalDetail
 * slideover. */
.dropdown-trigger.size-small {
  padding: 0.375rem 0.625rem;
  gap: 0.35rem;
  font-size: 0.75rem;
}

/* Reset-X — clears the filter without opening the menu. Replaces the
 * chevron when filtered. Component-specific affordance, not part of the
 * shared dropdown-trigger vocabulary. */
.reset-x {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  background: var(--surface-overlay-3);
  border: none;
  border-radius: var(--radius-pill);
  color: var(--text-muted);
  font-size: 0.5rem;
  cursor: pointer;
  transition: var(--transition-fast);
}

.dropdown-trigger.size-small .reset-x {
  width: 14px;
  height: 14px;
  font-size: 0.45rem;
}

.reset-x:hover {
  background: rgba(255, 255, 255, 0.15);
  color: var(--text-primary);
}

/* Dropdown menu */
.selector-menu {
  background: var(--surface-page);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  z-index: 9999;
  overflow: hidden;
  max-height: 400px;
  overflow-y: auto;
}

.menu-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  transition: background 0.15s ease;
}

.menu-option:hover {
  background: var(--surface-overlay);
}

.menu-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
  margin: 0.25rem 0;
}

.reset-option {
  color: var(--text-muted);
}

.reset-option:hover {
  color: var(--text-primary);
}

.reset-icon {
  font-size: 0.7rem;
  color: var(--text-muted);
}

.option-check-space {
  width: 1rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.option-label {
  font-size: 0.8125rem;
  color: var(--text-primary);
}

.option-group {
  font-size: 0.6875rem;
  color: var(--text-faint);
  flex-shrink: 0;
}

/* Checkbox */
.checkbox {
  width: 1rem;
  height: 1rem;
  border: 1.5px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.1875rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--transition-fast);
}

.checkbox.small {
  width: 0.875rem;
  height: 0.875rem;
}

.checkbox.is-checked {
  background: rgba(115, 87, 210, 0.6);
  border-color: var(--accent);
}

.checkbox.is-indeterminate {
  background: rgba(115, 87, 210, 0.3);
  border-color: rgba(115, 87, 210, 0.6);
}

.check-icon {
  font-size: 0.5rem;
  color: var(--text-heading);
}

.checkbox.small .check-icon {
  font-size: 0.45rem;
}

/* Source row */
.source-row {
  padding-right: 0.5rem;
}

.source-label {
  flex: 1;
  font-weight: 500;
}

.expand-chevron {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: var(--radius-xs);
  color: var(--text-faint);
  font-size: 0.5rem;
  cursor: pointer;
  transition: var(--transition-fast);
}

.expand-chevron:hover {
  background: rgba(255, 255, 255, 0.06);
  color: var(--text-primary);
}

/* View-mode toggle (owned groups): switch between member sources and group log types */
.view-mode-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: var(--radius-xs);
  color: var(--text-faint);
  font-size: 0.625rem;
  cursor: pointer;
  transition: var(--transition-fast);
  flex-shrink: 0;
}

.view-mode-toggle:hover {
  background: rgba(255, 255, 255, 0.06);
  color: var(--text-primary);
}

/* Member source rows (owned-group drill-in) */
.members-list {
  overflow: hidden;
}

.member-row {
  padding-left: 1.5rem;
  padding-top: 0.375rem;
  padding-bottom: 0.375rem;
}

.member-label {
  flex: 1;
  font-weight: 500;
}

/* Log type rows */
.log-types-list {
  overflow: hidden;
}

/* Member-nested log types sit one level deeper than group-level log types */
.member-logs .log-type-row {
  padding-left: 2.75rem;
}

.log-type-row {
  padding-left: 1.5rem;
  padding-top: 0.375rem;
  padding-bottom: 0.375rem;
}

.log-type-row.is-selected {
  background: rgba(115, 87, 210, 0.06);
}

.log-type-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  flex-shrink: 0;
}

/* Animations */
.selector-dropdown-enter-active,
.selector-dropdown-leave-active {
  transition: var(--transition-fast);
}

.selector-dropdown-enter-from,
.selector-dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.logs-expand-enter-active,
.logs-expand-leave-active {
  transition: var(--transition-fast);
  overflow: hidden;
}

.logs-expand-enter-from,
.logs-expand-leave-to {
  opacity: 0;
  max-height: 0;
}

.logs-expand-enter-to,
.logs-expand-leave-from {
  max-height: 500px;
}

/* Empty state */
.empty-sources {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.25rem 1rem;
  text-align: center;
  gap: 0.4rem;
}

.empty-icon {
  font-size: 1.1rem;
  color: var(--text-disabled);
}

.empty-text {
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-muted);
}

.empty-hint {
  font-size: 0.7rem;
  color: var(--text-faint);
  line-height: 1.4;
  max-width: 200px;
}

/* Scrollbar */
.selector-menu::-webkit-scrollbar {
  width: 4px;
}

.selector-menu::-webkit-scrollbar-track {
  background: transparent;
}

.selector-menu::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-xs);
}
</style>
